import { z } from "zod/v4";

import { AgenticLogicAttemptRecordDescriptions } from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";

// ---------------------------------------------------------------------------
// Per-attempt telemetry records
// ---------------------------------------------------------------------------
// Lives in types/ (not sdk/chain.ts, which produces them) so the evaluation
// telemetry contract can reference the record without a types -> sdk cycle.

/**
 * Per-attempt outcomes. Wire format — append-only.
 *  - contributed: ran, and >=1 of its items made the final result.
 *  - empty: ran and returned zero items.
 *  - not-selected: ran (exhaustive mode, or a later fillTo branch) but none
 *    of its items were used.
 *  - skipped: never executed (chain already satisfied, normal mode).
 *  - error: threw; the chain records it and falls through to the next branch.
 */
export const AgenticLogicAttemptOutcomeList = [
  "contributed",
  "empty",
  "not-selected",
  "skipped",
  "error",
] as const;
export const AgenticLogicAttemptOutcomeSchema = z.enum(
  AgenticLogicAttemptOutcomeList
);
export type AgenticLogicAttemptOutcome =
  (typeof AgenticLogicAttemptOutcomeList)[number];

export const AgenticLogicAttemptRecordSchema = z
  .object({
    base: z.string().describe(AgenticLogicAttemptRecordDescriptions.base),
    instance: z
      .string()
      .optional()
      .describe(AgenticLogicAttemptRecordDescriptions.instance),
    contributed: z
      .number()
      .int()
      .nonnegative()
      .describe(AgenticLogicAttemptRecordDescriptions.contributed),
    outcome: AgenticLogicAttemptOutcomeSchema.describe(
      AgenticLogicAttemptRecordDescriptions.outcome
    ),
  })
  .describe(AgenticLogicAttemptRecordDescriptions._schema);
export type AgenticLogicAttemptRecord = z.infer<
  typeof AgenticLogicAttemptRecordSchema
>;

assertDescriptions<
  typeof AgenticLogicAttemptRecordDescriptions,
  AgenticLogicAttemptRecord
>();
